/**
 * Point cloud styling types and enums
 */
export const MAX_CLASSIFICATION_ID = 255;
export const DEFAULT_CLASSIFICATION_COLOR = 'EBEBEBFF';

export interface PointCloudClassDefinition {
  id: number;
  color: string;
  visibility: boolean;
}

export interface PointCloudStylingClassification {
  classDefinitions?: PointCloudClassDefinition[];
}

export interface PointCloudStylingBand {
  start: number;
  color: string;
}

export interface PointCloudStylingClearance {
  bands?: PointCloudStylingBand[];
  defaultColor?: string;
  defaultTransparent?: boolean;
}

export interface PointCloudStylingElevation {
  reference?: number;
  offset?: number;
  bandCount?: number;
  tolerance?: number;
}

export interface PointCloudStylingEyeDomeLighting {
  enabled?: boolean;
  radius?: number;
  strength?: number;
}

/**
 * The startColor is 0.0 of the gradient, endColor is determined by the offset which should be less than 1.0
 */
export interface PointCloudStylingIntensityGradient {
  startColor: string;
  endColor: string;
  /** Must be between 0.0 and 1.0 */
  offset: number;
}

export interface PointCloudStylingIntensity {
  blendIntensity?: boolean;
  gradient?: PointCloudStylingIntensityGradient;
}

export interface PointCloudStylingPoint {
  budget?: number;
  densityBias?: number;
  size?: number;
  shape?: PointCloudStylingPointShape;
  sizeAttenuation?: boolean;
}

export enum PointCloudStylingType {
  Rgb = 'Rgb',
  Classification = 'Classification',
  Clearance = 'Clearance',
  Elevation = 'Elevation',
  Intensity = 'Intensity',
}

export enum PointCloudStylingPointShape {
  Square = 'Square',
  Circle = 'Circle',
  Paraboloide = 'Paraboloide',
}

export const PointColorType = {
  RGB: 0,
  ELEVATION: 3,
  INTENSITY: 4,
  CLASSIFICATION: 8,
  RGB_HEIGHT: 13,
  INTENSITY_CLASSIFICATION: 14,
  CLEARANCE: 15,
} as const;

export const PointShape = {
  SQUARE: 0,
  CIRCLE: 1,
  PARABOLOID: 2,
} as const;

export enum ShadingOptions {
  DEFAULT = 'DEFAULT',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  EYE_DOME_LIGHTING = 'EYE_DOME_LIGHTING',
}

export interface PointCloudStyling {
  stylingType?: PointCloudStylingType;
  classification?: PointCloudStylingClassification;
  clearance?: PointCloudStylingClearance;
  elevation?: PointCloudStylingElevation;
  eyeDomeLighting?: PointCloudStylingEyeDomeLighting;
  intensity?: PointCloudStylingIntensity;
  point?: PointCloudStylingPoint;
}

export interface PointCloudStylingUOM {
  unitLabel: string;
  meterToUnitFactor: number;
  lengthDecimals?: number;
  feetInches?: boolean;
  lengthFractions?: number;
}

export const DEFAULT_POINT_DENSITY_BIAS = 1.5;

// web3d divides the point size by the density bias.
export const isValidPointDensityBias = (
  densityBias: number | undefined
): densityBias is number => densityBias !== undefined && densityBias > 0;

export const POINT_CLOUD_STYLING_DEFAULTS: PointCloudStyling = {
  point: {
    budget: 30000000,
    densityBias: DEFAULT_POINT_DENSITY_BIAS,
    size: 2,
  },
  eyeDomeLighting: {
    enabled: true,
  },
  stylingType: PointCloudStylingType.Rgb,
  clearance: {
    bands: [
      { start: -0.4, color: '#ff0000' },
      { start: 0, color: '#ff7f00' },
      { start: 0.1, color: '#ffff00' },
      { start: 0.2, color: '#00ff00' },
      { start: 0.3, color: '#0000ff' },
      { start: 0.4, color: '#000000' }, // Last color value is ignored
    ],

    defaultColor: '#C8A2C8',
    defaultTransparent: false,
  },
  elevation: {
    reference: undefined,
    tolerance: 1,
    offset: 0,
    bandCount: 1,
  },
  intensity: {
    gradient: {
      startColor: '#000000',
      endColor: '#ffffff',
      offset: 0.5,
    },
  },
};

export interface ClassificationScheme {
  id: number;
  name: string;
  rgba: string;
  visible: boolean;
  predefined: boolean;
  /** True when the scheme was synthesised client-side for a missing class. */
  isNew?: boolean;
}

export interface Scan3dStyle {
  renderingMode: PointCloudStylingType;
  blendIntensity: boolean;
  showEyeDomeLighting: boolean;
  pointBudget: number;
  pointDensity: number;
  pointSize: number;
  classificationSchemes: ClassificationScheme[];
  intensityOffset: number;
  intensityStartColor: string; // hex color
  intensityEndColor: string; // hex color
  clearanceDefaultTransparent: boolean;
  clearanceDefaultColor: string; // hex color
  clearanceBands: PointCloudStylingBand[];
  elevationReference: number;
  elevationTolerance: number;
  elevationBandCount: number;
  elevationOffset: number;
}
